import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { format, isValid, parseISO } from 'date-fns'
import { CircleAlert, Loader2, Send, UserRound } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { useIsMobile } from '@/hooks/useIsMobile'
import {
  getPopoverMaxHeightInDirection,
  getTooltipPositionClass,
} from '@/lib/utils/tooltipPosition'
import { YARDI_GUEST_INVITE_CONTACT_INFO_MESSAGE } from '../constants'
import { guestCardOccupantName, guestCardProspectKey } from '../utils/yardiGuestCardGrouping'
import { isResponsibleForLease } from '../utils/yardiGuestCardStatus'
import {
  formatValidationErrorTitle,
  getEmailValidationState,
  getPhoneValidationState,
  getValidationColumnHeaderLabel,
  maskformatPhoneNumber,
  parseValidationErrorMessage,
} from '../utils/yardiInviteUi'
import modalStyles from '../styles/yardiGuestInviteModal.module.css'
import type {
  YardiGuestCardRow,
  YardiGuestInviteModalProps,
  YardiGuestInviteValidateResponse,
  YardiGuestInviteValidationError,
} from '../types'

function isSelfRelationship(type: string | null | undefined): boolean {
  return String(type ?? '').trim().toUpperCase() === 'SELF'
}

function ContactEmpty({
  label,
  severity = 'error',
}: {
  label: string
  severity?: 'error' | 'warning' | 'neutral'
}) {
  if (severity === 'warning') {
    return <span className={modalStyles.warningValueBadge}>{label}</span>
  }
  if (severity === 'neutral') {
    return <span className={modalStyles.cellEmpty}>{label}</span>
  }
  return <span className={modalStyles.missingBadge}>{label}</span>
}

function isWarningSeverity(err: YardiGuestInviteValidationError): boolean {
  return err.severity === 'warning'
}

function hasValidationFieldError(
  errors: YardiGuestInviteValidationError[],
  ...fields: string[]
): boolean {
  const fieldSet = new Set(fields)
  return errors.some((err) => fieldSet.has(err.field) && !isWarningSeverity(err))
}

/** Empty contact / invalid value → red error, amber warning, or plain text. */
function ContactValue({
  value,
  emptyLabel,
  hasError,
  hasWarning = false,
}: {
  value: string
  emptyLabel: string
  hasError: boolean
  hasWarning?: boolean
}) {
  if (!value) {
    return (
      <ContactEmpty
        label={emptyLabel}
        severity={hasError ? 'error' : hasWarning ? 'warning' : 'neutral'}
      />
    )
  }
  if (hasError) {
    return <span className={modalStyles.contactErrorValue}>{value}</span>
  }
  if (hasWarning) {
    return <span className={modalStyles.warningValueBadge}>{value}</span>
  }
  return <span className={modalStyles.contactValue}>{value}</span>
}

function displayCreditCheck(status: string | null | undefined): string {
  const trimmed = String(status ?? '').trim()
  return trimmed || '—'
}

function formatShortDate(v: string | null | undefined): string {
  if (!v) return ''
  try {
    const d = parseISO(String(v).replace(' ', 'T'))
    if (isValid(d)) return format(d, 'MM/dd/yyyy')
    const d2 = new Date(v)
    if (isValid(d2)) return format(d2, 'MM/dd/yyyy')
  } catch {
    /* ignore */
  }
  return String(v)
}

function formatRentAmount(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  const num = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''))
  if (!Number.isFinite(num)) return String(value)
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(num)
}

function ValidationErrorMessage({ message }: { message: string }) {
  const segments = parseValidationErrorMessage(message)
  if (segments.length === 1 && !segments[0]?.bold) {
    return <>{message}</>
  }
  return (
    <>
      {segments.map((segment, idx) =>
        segment.bold ? (
          <strong key={`${idx}-${segment.text}`}>{segment.text}</strong>
        ) : (
          <span key={`${idx}-${segment.text}`}>{segment.text}</span>
        ),
      )}
    </>
  )
}

function ValidationTooltipProspect({ subtitle }: { subtitle: string }) {
  const match = subtitle.match(/^Prospect ID:\s*(.+)$/i)
  if (!match) {
    return <p className={modalStyles.errorTooltipProspect}>{subtitle}</p>
  }
  return (
    <p className={modalStyles.errorTooltipProspect}>
      Prospect ID: <strong>{match[1]}</strong>
    </p>
  )
}

function getValidationTooltipTitle(
  level: 'application' | 'applicant',
  severity: 'error' | 'warning',
): string {
  if (level === 'application') {
    return severity === 'warning' ? 'Application level warning' : 'Application level error'
  }
  return severity === 'warning' ? 'Applicant level warning' : 'Applicant level error'
}

function ValidationErrorBadge({
  errors,
  rowKey,
  level,
  severity,
  tooltipSubtitle,
  isLastRow = false,
}: {
  errors: YardiGuestInviteValidationError[]
  rowKey: string
  level: 'application' | 'applicant'
  severity: 'error' | 'warning'
  tooltipSubtitle?: string
  isLastRow?: boolean
}) {
  const isMobile = useIsMobile()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{
    top?: number
    left: number
    bottom?: number
    maxHeight: number
    transform?: string
    arrowSide?: 'left' | 'right'
    arrowOffset?: number
    showArrow: boolean
  } | null>(null)

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])

  useEffect(() => () => clearHideTimer(), [clearHideTimer])

  const scheduleHide = useCallback(() => {
    if (isMobile) return
    clearHideTimer()
    hideTimerRef.current = setTimeout(() => {
      hideTimerRef.current = null
      setOpen(false)
    }, 200)
  }, [clearHideTimer, isMobile])

  const updatePosition = useCallback(() => {
    const el = triggerRef.current
    if (!el) return

    const rect = el.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const tooltipWidth = Math.min(320, viewportWidth - 24)
    const padding = 12
    const globalMaxHeight = Math.min(280, viewportHeight - 24)

    if (isMobile) {
      const openUp =
        getTooltipPositionClass(isLastRow, el, viewportHeight, globalMaxHeight) === 'top'
      const maxHeight = getPopoverMaxHeightInDirection(el, openUp, viewportHeight, globalMaxHeight)
      const left = Math.max(
        padding,
        Math.min(rect.left, viewportWidth - tooltipWidth - padding),
      )

      if (openUp) {
        setCoords({
          left,
          bottom: viewportHeight - rect.top + 8,
          maxHeight,
          showArrow: false,
        })
        return
      }

      setCoords({
        left,
        top: rect.bottom + 8,
        maxHeight,
        showArrow: false,
      })
      return
    }

    let left = rect.left - 8 - tooltipWidth
    let arrowSide: 'left' | 'right' = 'right'
    if (left < padding) {
      left = Math.min(rect.right + 8, viewportWidth - tooltipWidth - padding)
      arrowSide = 'left'
    }

    const badgeCenter = rect.top + rect.height / 2
    const top = Math.max(padding, Math.min(badgeCenter, viewportHeight - padding))
    const arrowOffset = Math.max(-110, Math.min(badgeCenter - top, 110))

    setCoords({
      top,
      left,
      maxHeight: globalMaxHeight,
      transform: 'translateY(-50%)',
      arrowSide,
      arrowOffset,
      showArrow: true,
    })
  }, [isLastRow, isMobile])

  const show = useCallback(() => {
    clearHideTimer()
    setOpen(true)
  }, [clearHideTimer])

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }
    updatePosition()
  }, [open, updatePosition, errors.length])

  useEffect(() => {
    if (!open) return
    const sync = () => updatePosition()
    window.addEventListener('scroll', sync, true)
    window.addEventListener('resize', sync)
    return () => {
      window.removeEventListener('scroll', sync, true)
      window.removeEventListener('resize', sync)
    }
  }, [open, updatePosition])

  useEffect(() => {
    if (!open || !isMobile) return
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target) || tooltipRef.current?.contains(target)) {
        return
      }
      setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
    }
  }, [open, isMobile])

  if (!errors.length) {
    return null
  }

  const isWarning = severity === 'warning'
  const count = errors.length
  const label = isWarning
    ? `${count} warning${count === 1 ? '' : 's'}`
    : `${count} error${count === 1 ? '' : 's'}`
  const tooltipTitle = getValidationTooltipTitle(level, severity)
  const badgeTestId = isWarning
    ? `yardi-guest-invite-modal-warning-badge-${rowKey}`
    : `yardi-guest-invite-modal-error-badge-${rowKey}`
  const tooltipTestId = isWarning
    ? `yardi-guest-invite-modal-warning-tooltip-${rowKey}`
    : `yardi-guest-invite-modal-error-tooltip-${rowKey}`

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={isWarning ? modalStyles.warningBadge : modalStyles.errorBadge}
        onMouseEnter={isMobile ? undefined : show}
        onMouseLeave={isMobile ? undefined : scheduleHide}
        onFocus={isMobile ? undefined : show}
        onBlur={isMobile ? undefined : scheduleHide}
        onClick={
          isMobile
            ? (event) => {
                event.stopPropagation()
                setOpen((current) => !current)
              }
            : undefined
        }
        aria-expanded={open}
        aria-label={label}
        data-testid={badgeTestId}
      >
        {label}
      </button>
      {open &&
        coords &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            className={modalStyles.errorTooltip}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              bottom: coords.bottom,
              maxHeight: coords.maxHeight,
              transform: coords.transform,
            }}
            onMouseEnter={isMobile ? undefined : clearHideTimer}
            onMouseLeave={isMobile ? undefined : scheduleHide}
            data-testid={tooltipTestId}
          >
            {coords.showArrow && coords.arrowSide ? (
              <span
                aria-hidden
                className={`${modalStyles.errorTooltipArrow} ${
                  coords.arrowSide === 'right'
                    ? modalStyles.errorTooltipArrowRight
                    : modalStyles.errorTooltipArrowLeft
                }`}
                style={{ top: `calc(50% + ${coords.arrowOffset ?? 0}px)` }}
              />
            ) : null}
            <div className={modalStyles.errorTooltipHeader}>
              <p className={modalStyles.errorTooltipTitle}>{tooltipTitle}</p>
              {tooltipSubtitle ? (
                <ValidationTooltipProspect subtitle={tooltipSubtitle} />
              ) : null}
            </div>
            <div className={modalStyles.errorTooltipList}>
              {errors.map((err, idx) => (
                <div
                  key={`${err.type}-${idx}`}
                  className={
                    isWarning ? modalStyles.warningTooltipItem : modalStyles.errorTooltipItem
                  }
                >
                  <span
                    className={isWarning ? modalStyles.warningTooltipIcon : modalStyles.errorTooltipIcon}
                    aria-hidden
                  >
                    <CircleAlert className="h-4 w-4" strokeWidth={2.25} />
                  </span>
                  <div className={modalStyles.errorTooltipItemBody}>
                    <p className={modalStyles.errorTooltipItemTitle}>
                      {formatValidationErrorTitle(err.type)}
                    </p>
                    <p className={modalStyles.errorTooltipItemMessage}>
                      <ValidationErrorMessage message={err.message} />
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}

function ValidationErrorCell({
  errors,
  rowKey,
  level,
  tooltipSubtitle,
  isLastRow = false,
}: {
  errors: YardiGuestInviteValidationError[]
  rowKey: string
  level: 'application' | 'applicant'
  tooltipSubtitle?: string
  isLastRow?: boolean
}) {
  const errorItems = errors.filter((err) => !isWarningSeverity(err))
  const warningItems = errors.filter(isWarningSeverity)

  if (!errorItems.length && !warningItems.length) {
    return null
  }

  return (
    <div className={modalStyles.validationBadgeGroup}>
      {errorItems.length > 0 ? (
        <ValidationErrorBadge
          errors={errorItems}
          rowKey={rowKey}
          level={level}
          severity="error"
          tooltipSubtitle={tooltipSubtitle}
          isLastRow={isLastRow}
        />
      ) : null}
      {warningItems.length > 0 ? (
        <ValidationErrorBadge
          errors={warningItems}
          rowKey={rowKey}
          level={level}
          severity="warning"
          tooltipSubtitle={tooltipSubtitle}
          isLastRow={isLastRow}
        />
      ) : null}
    </div>
  )
}

function RelationshipBadge({ type }: { type: string | null | undefined }) {
  const trimmed = String(type ?? '').trim()
  if (!trimmed) return <span className={modalStyles.cellEmpty}>—</span>
  const self = isSelfRelationship(trimmed)
  return (
    <span
      className={`${modalStyles.relationshipBadge} ${self ? modalStyles.relationshipSelf : modalStyles.relationshipOther}`}
    >
      {trimmed}
    </span>
  )
}

function ApplicationValueDisplay({
  value,
  hasError = false,
  hasWarning = false,
  emptyLabel,
}: {
  value: string
  hasError?: boolean
  hasWarning?: boolean
  emptyLabel?: string
}) {
  if (hasError) {
    if (value) {
      return <span className={modalStyles.contactErrorValue}>{value}</span>
    }
    return <ContactEmpty label={emptyLabel || 'Missing'} />
  }
  if (hasWarning && value) {
    return <span className={modalStyles.warningValueBadge}>{value}</span>
  }
  if (value) return <>{value}</>
  return <span className={modalStyles.cellEmpty}>—</span>
}

function ApplicationValueCell({
  value,
  testId,
  hasError = false,
  hasWarning = false,
  emptyLabel,
}: {
  value: string
  testId: string
  hasError?: boolean
  hasWarning?: boolean
  emptyLabel?: string
}) {
  return (
    <td data-testid={testId}>
      <ApplicationValueDisplay
        value={value}
        hasError={hasError}
        hasWarning={hasWarning}
        emptyLabel={emptyLabel}
      />
    </td>
  )
}

function ApplicationMobileField({
  label,
  value,
  testId,
  hasError = false,
  hasWarning = false,
  emptyLabel,
}: {
  label: string
  value: string
  testId: string
  hasError?: boolean
  hasWarning?: boolean
  emptyLabel?: string
}) {
  return (
    <div className={modalStyles.mobileField} data-testid={testId}>
      <span className={modalStyles.mobileLabel}>{label}</span>
      <span className={modalStyles.mobileValue}>
        <ApplicationValueDisplay
          value={value}
          hasError={hasError}
          hasWarning={hasWarning}
          emptyLabel={emptyLabel}
        />
      </span>
    </div>
  )
}

function ApplicationLevelRow({
  validationResult,
  isMobile,
}: {
  validationResult: YardiGuestInviteValidateResponse | null | undefined
  isMobile: boolean
}) {
  const validated = validationResult?.application
  const leaseStartDate = formatShortDate(validated?.leaseStartDate)
  const targetMoveInDate = formatShortDate(validated?.targetMoveInDate)
  const expectedMoveInDate = formatShortDate(validated?.expectedMoveInDate)
  const rent = formatRentAmount(validated?.rentAmount)
  const unitNumber = String(validated?.unitNumber ?? '').trim()
  const leaseDuration = String(validated?.leaseDuration ?? '').trim()
  const applicationErrors = validationResult?.applicationErrors ?? []
  const erroredFields = new Set(
    applicationErrors.filter((err) => !isWarningSeverity(err)).map((err) => err.field),
  )
  const warningFields = new Set(
    applicationErrors.filter(isWarningSeverity).map((err) => err.field),
  )
  const applicationErrorColumnLabel = getValidationColumnHeaderLabel(applicationErrors)
  const showErrorColumn = applicationErrors.length > 0

  if (isMobile) {
    return (
      <div
        className={`${modalStyles.mobileCard} ${modalStyles.applicationMobileCard}`}
        data-testid="yardi-guest-invite-modal-application-row"
      >
        <div className={modalStyles.mobileGrid} data-testid="yardi-guest-invite-modal-application-mobile">
          <ApplicationMobileField
            label="Lease Start Date"
            value={leaseStartDate}
            testId="yardi-guest-invite-modal-app-lease-start"
            hasError={erroredFields.has('leaseStartDate')}
            hasWarning={warningFields.has('leaseStartDate')}
            emptyLabel="No lease start date"
          />
          <ApplicationMobileField
            label="Target Move In Date"
            value={targetMoveInDate}
            testId="yardi-guest-invite-modal-app-target-move-in"
          />
          <ApplicationMobileField
            label="Expected Move In Date"
            value={expectedMoveInDate}
            testId="yardi-guest-invite-modal-app-expected-move-in"
          />
          <ApplicationMobileField
            label="Monthly Rent"
            value={rent}
            testId="yardi-guest-invite-modal-app-rent"
            hasError={erroredFields.has('rentAmount')}
            emptyLabel="No rent amount"
          />
          <ApplicationMobileField
            label="Unit Number"
            value={unitNumber}
            testId="yardi-guest-invite-modal-app-unit"
            hasError={erroredFields.has('unitNumber')}
            emptyLabel="No unit number"
          />
          <ApplicationMobileField
            label="Lease Duration"
            value={leaseDuration}
            testId="yardi-guest-invite-modal-app-lease-duration"
          />
          {showErrorColumn ? (
            <div className={modalStyles.mobileField} data-testid="yardi-guest-invite-modal-application-errors">
              <span className={modalStyles.mobileLabel}>{applicationErrorColumnLabel}</span>
              <span className={modalStyles.mobileValue}>
                <ValidationErrorCell
                  errors={applicationErrors}
                  rowKey="application"
                  level="application"
                />
              </span>
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  const columnGroup = (
    <colgroup>
      <col className={modalStyles.colAppLeaseStart} />
      <col className={modalStyles.colAppTargetMoveIn} />
      <col className={modalStyles.colAppExpectedMoveIn} />
      <col className={modalStyles.colAppRent} />
      <col className={modalStyles.colAppUnit} />
      <col className={modalStyles.colAppLeaseDuration} />
      {showErrorColumn ? <col className={modalStyles.colAppErrors} /> : null}
    </colgroup>
  )

  return (
    <div
      className={`${modalStyles.tableCard} ${modalStyles.applicationTableCard}`}
      data-testid="yardi-guest-invite-modal-application-row"
    >
      <div className={modalStyles.tableHeader} data-testid="yardi-guest-invite-modal-application-table-head">
        <table className={modalStyles.table} data-testid="yardi-guest-invite-modal-application-table-header">
          {columnGroup}
          <thead>
            <tr>
              <th data-testid="yardi-guest-invite-modal-col-app-lease-start">Lease Start Date</th>
              <th data-testid="yardi-guest-invite-modal-col-app-target-move-in">Target Move In Date</th>
              <th data-testid="yardi-guest-invite-modal-col-app-expected-move-in">Expected Move In Date</th>
              <th data-testid="yardi-guest-invite-modal-col-app-rent">Monthly Rent</th>
              <th data-testid="yardi-guest-invite-modal-col-app-unit">Unit Number</th>
              <th data-testid="yardi-guest-invite-modal-col-app-lease-duration">Lease Duration</th>
              {showErrorColumn ? (
                <th data-testid="yardi-guest-invite-modal-col-app-errors">{applicationErrorColumnLabel}</th>
              ) : null}
            </tr>
          </thead>
        </table>
      </div>
      <div className={modalStyles.tableWrap} data-testid="yardi-guest-invite-modal-application-table-scroll">
        <table className={modalStyles.table} data-testid="yardi-guest-invite-modal-application-table">
          {columnGroup}
          <tbody data-testid="yardi-guest-invite-modal-application-table-body">
            <tr data-testid="yardi-guest-invite-modal-application-data-row">
              <ApplicationValueCell
                value={leaseStartDate}
                testId="yardi-guest-invite-modal-app-lease-start"
                hasError={erroredFields.has('leaseStartDate')}
                hasWarning={warningFields.has('leaseStartDate')}
                emptyLabel="No lease start date"
              />
              <ApplicationValueCell
                value={targetMoveInDate}
                testId="yardi-guest-invite-modal-app-target-move-in"
              />
              <ApplicationValueCell
                value={expectedMoveInDate}
                testId="yardi-guest-invite-modal-app-expected-move-in"
              />
              <ApplicationValueCell
                value={rent}
                testId="yardi-guest-invite-modal-app-rent"
                hasError={erroredFields.has('rentAmount')}
                emptyLabel="No rent amount"
              />
              <ApplicationValueCell
                value={unitNumber}
                testId="yardi-guest-invite-modal-app-unit"
                hasError={erroredFields.has('unitNumber')}
                emptyLabel="No unit number"
              />
              <ApplicationValueCell
                value={leaseDuration}
                testId="yardi-guest-invite-modal-app-lease-duration"
              />
              {showErrorColumn ? (
                <td data-testid="yardi-guest-invite-modal-application-errors">
                  <ValidationErrorCell
                    errors={applicationErrors}
                    rowKey="application"
                    level="application"
                  />
                </td>
              ) : null}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ModalFooter({
  sending,
  sendPhase,
  validating,
  canSend,
  selectedCount,
  onClose,
  onRevalidate,
  onSendInvite,
}: {
  sending: boolean
  sendPhase: 'idle' | 'submitting' | 'processing'
  validating: boolean
  canSend: boolean
  selectedCount: number
  onClose: () => void
  onRevalidate?: () => void
  onSendInvite: () => void
}) {
  const isMobile = useIsMobile()

  return (
    <div
      className={modalStyles.footer}
      data-testid="yardi-guest-invite-modal-footer"
    >
      <span className={modalStyles.footerMeta} data-testid="yardi-guest-invite-modal-selected-count">
        {selectedCount} applicant{selectedCount === 1 ? '' : 's'} selected
      </span>
      <div className={modalStyles.footerActions}>
        <button
          type="button"
          disabled={sending || validating}
          onClick={onClose}
          data-testid="yardi-guest-invite-modal-cancel"
          className={`${modalStyles.footerBtn} ${modalStyles.footerBtnCancel}`}
        >
          Cancel
        </button>
        {onRevalidate ? (
          <button
            type="button"
            disabled={sending || validating}
            onClick={() => void onRevalidate()}
            data-testid="yardi-guest-invite-modal-revalidate"
            className={`${modalStyles.footerBtn} ${modalStyles.footerBtnRevalidate}`}
          >
            {validating ? (
              <>
                <Loader2 className="inline h-4 w-4 animate-spin mr-1.5" aria-hidden />
                Validating…
              </>
            ) : (
              'Re-Validate'
            )}
          </button>
        ) : null}
        <button
          type="button"
          disabled={sending || validating || !canSend || selectedCount === 0}
          onClick={() => void onSendInvite()}
          data-testid="yardi-guest-invite-modal-send"
          className={`${modalStyles.footerBtn} ${modalStyles.footerBtnSend}`}
        >
          {sending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {sendPhase === 'processing' ? 'Processing…' : 'Submitting…'}
            </>
          ) : (
            <>
              <Send className="h-4 w-4" aria-hidden />
              {isMobile ? 'Send' : 'Send Invite'}
            </>
          )}
        </button>
      </div>
    </div>
  )
}

function DesktopOccupantTable({
  occupants,
  selectedProspectIds,
  sending,
  validating,
  applicantErrorsByProspectId,
  onToggleOccupant,
}: {
  occupants: YardiGuestCardRow[]
  selectedProspectIds: string[]
  sending: boolean
  validating: boolean
  applicantErrorsByProspectId: Map<string, YardiGuestInviteValidationError[]>
  onToggleOccupant: (prospectId: string) => void
}) {
  const allApplicantErrors = Array.from(applicantErrorsByProspectId.values()).flat()
  const showErrorColumn = allApplicantErrors.length > 0
  const applicantErrorColumnLabel = getValidationColumnHeaderLabel(allApplicantErrors, 'Error')
  const columnGroup = (
    <colgroup>
      <col className={modalStyles.colSelect} />
      <col className={modalStyles.colProspect} />
      <col className={modalStyles.colTenant} />
      <col className={modalStyles.colName} />
      <col className={modalStyles.colEmail} />
      <col className={modalStyles.colPhone} />
      <col className={modalStyles.colCredit} />
      <col className={modalStyles.colRelationship} />
      {showErrorColumn ? <col className={modalStyles.colError} /> : null}
    </colgroup>
  )

  return (
    <div className={modalStyles.tableCard}>
      <div className={modalStyles.tableHeader} data-testid="yardi-guest-invite-modal-table-head">
        <table className={modalStyles.table} data-testid="yardi-guest-invite-modal-table-header">
          {columnGroup}
          <thead>
            <tr>
              <th className={modalStyles.colSelect} data-testid="yardi-guest-invite-modal-col-select">
                <span className={modalStyles.colSelectLabel}>Liberty Approval Needed?</span>
              </th>
              <th data-testid="yardi-guest-invite-modal-col-prospect-id">Prospect ID</th>
              <th data-testid="yardi-guest-invite-modal-col-tenant-id">Tenant ID</th>
              <th data-testid="yardi-guest-invite-modal-col-name">Name</th>
              <th data-testid="yardi-guest-invite-modal-col-email">Email</th>
              <th data-testid="yardi-guest-invite-modal-col-phone">Phone</th>
              <th data-testid="yardi-guest-invite-modal-col-credit-check">Credit Check Status</th>
              <th data-testid="yardi-guest-invite-modal-col-relationship">Relationship Type</th>
              {showErrorColumn ? (
                <th className={modalStyles.colError} data-testid="yardi-guest-invite-modal-col-error">
                  {applicantErrorColumnLabel}
                </th>
              ) : null}
            </tr>
          </thead>
        </table>
      </div>
      <div className={modalStyles.tableWrap} data-testid="yardi-guest-invite-modal-table-scroll">
        <table className={modalStyles.table} data-testid="yardi-guest-invite-modal-table">
          {columnGroup}
          <tbody data-testid="yardi-guest-invite-modal-table-body">
            {occupants.map((row, idx) => {
              const prospectKey = guestCardProspectKey(row)
              const rowKey = prospectKey || `row-${idx}`
              const selectableRow = isResponsibleForLease(row)
              const notResponsibleForLease = row.responsibleForLease === false
              const checked = selectedProspectIds.includes(prospectKey)
              const email = String(row.email ?? '').trim()
              const phoneRaw = String(row.mobileNo ?? '').trim()
              const phone = phoneRaw ? maskformatPhoneNumber(phoneRaw) : ''
              const name = guestCardOccupantName(row)
              const rowErrors = applicantErrorsByProspectId.get(prospectKey.toUpperCase()) ?? []
              const nameHasError = hasValidationFieldError(
                rowErrors,
                'firstName',
                'middleName',
                'lastName',
              )
              const { hasError: emailHasError, hasWarning: emailHasWarning } = getEmailValidationState(
                rowErrors,
                !email,
              )
              const { hasError: phoneHasError, hasWarning: phoneHasWarning } = getPhoneValidationState(
                rowErrors,
                !phoneRaw,
              )
              return (
                <tr
                  key={rowKey}
                  className={
                    notResponsibleForLease
                      ? modalStyles.rowDisabled
                      : checked
                        ? modalStyles.rowSelected
                        : undefined
                  }
                  aria-disabled={notResponsibleForLease || undefined}
                  data-testid={`yardi-guest-invite-modal-row-${rowKey}`}
                >
                  <td className={modalStyles.colSelect}>
                    <input
                      type="checkbox"
                      className={modalStyles.checkbox}
                      checked={checked}
                      disabled={sending || validating || !selectableRow}
                      onChange={() => onToggleOccupant(prospectKey)}
                      data-testid={`yardi-guest-invite-modal-select-${rowKey}`}
                      aria-label={`Liberty approval needed for ${name || prospectKey}`}
                    />
                  </td>
                  <td
                    className={modalStyles.cellMono}
                    data-testid={`yardi-guest-invite-modal-cell-prospect-id-${rowKey}`}
                  >
                    {prospectKey ? String(row.prospectId ?? '').trim() || '—' : '—'}
                  </td>
                  <td
                    className={row.tenantId ? modalStyles.cellMono : modalStyles.cellEmpty}
                    data-testid={`yardi-guest-invite-modal-cell-tenant-id-${rowKey}`}
                  >
                    {String(row.tenantId ?? '').trim() || '—'}
                  </td>
                  <td
                    className={modalStyles.cellContact}
                    data-testid={`yardi-guest-invite-modal-cell-name-${rowKey}`}
                  >
                    <ContactValue value={name} emptyLabel="No name" hasError={nameHasError} />
                  </td>
                  <td
                    className={modalStyles.cellContact}
                    data-testid={`yardi-guest-invite-modal-cell-email-${rowKey}`}
                  >
                    <ContactValue
                      value={email}
                      emptyLabel="No email"
                      hasError={emailHasError}
                      hasWarning={emailHasWarning}
                    />
                  </td>
                  <td
                    className={`${modalStyles.cellContact}${phone && !phoneHasError && !phoneHasWarning ? ` ${modalStyles.cellPhone}` : ''}`}
                    data-testid={`yardi-guest-invite-modal-cell-phone-${rowKey}`}
                  >
                    <ContactValue
                      value={phone}
                      emptyLabel="No phone"
                      hasError={phoneHasError}
                      hasWarning={phoneHasWarning}
                    />
                  </td>
                  <td
                    className={modalStyles.cellCredit}
                    data-testid={`yardi-guest-invite-modal-cell-credit-check-${rowKey}`}
                  >
                    {displayCreditCheck(row.creditCheckStatus)}
                  </td>
                  <td
                    className={modalStyles.colRelationship}
                    data-testid={`yardi-guest-invite-modal-cell-relationship-${rowKey}`}
                  >
                    <RelationshipBadge type={row.type} />
                  </td>
                  {showErrorColumn ? (
                    <td
                      className={modalStyles.colError}
                      data-testid={`yardi-guest-invite-modal-cell-error-${rowKey}`}
                    >
                      <ValidationErrorCell
                        errors={rowErrors}
                        rowKey={rowKey}
                        level="applicant"
                        tooltipSubtitle={
                          String(row.prospectId ?? '').trim()
                            ? `Prospect ID: ${String(row.prospectId).trim()}`
                            : undefined
                        }
                      />
                    </td>
                  ) : null}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function MobileOccupantCards({
  occupants,
  selectedProspectIds,
  sending,
  validating,
  applicantErrorsByProspectId,
  onToggleOccupant,
}: {
  occupants: YardiGuestCardRow[]
  selectedProspectIds: string[]
  sending: boolean
  validating: boolean
  applicantErrorsByProspectId: Map<string, YardiGuestInviteValidationError[]>
  onToggleOccupant: (prospectId: string) => void
}) {
  return (
    <div className={modalStyles.mobileList} data-testid="yardi-guest-invite-modal-mobile-list">
      {occupants.map((row, idx) => {
        const prospectKey = guestCardProspectKey(row)
        const rowKey = prospectKey || `row-${idx}`
        const selectableRow = isResponsibleForLease(row)
        const notResponsibleForLease = row.responsibleForLease === false
        const checked = selectedProspectIds.includes(prospectKey)
        const email = String(row.email ?? '').trim()
        const phoneRaw = String(row.mobileNo ?? '').trim()
        const phone = phoneRaw ? maskformatPhoneNumber(phoneRaw) : ''
        const name = guestCardOccupantName(row)
        const rowErrors = applicantErrorsByProspectId.get(prospectKey.toUpperCase()) ?? []
        const nameHasError = hasValidationFieldError(
          rowErrors,
          'firstName',
          'middleName',
          'lastName',
        )
        const { hasError: emailHasError, hasWarning: emailHasWarning } = getEmailValidationState(
          rowErrors,
          !email,
        )
        const { hasError: phoneHasError, hasWarning: phoneHasWarning } = getPhoneValidationState(
          rowErrors,
          !phoneRaw,
        )
        const rowErrorColumnLabel = getValidationColumnHeaderLabel(rowErrors, 'Error')

        return (
          <div
            key={rowKey}
            className={`${modalStyles.mobileCard}${notResponsibleForLease ? ` ${modalStyles.mobileCardDisabled}` : checked ? ` ${modalStyles.mobileCardSelected}` : ''}`}
            aria-disabled={notResponsibleForLease || undefined}
            data-testid={`yardi-guest-invite-modal-mobile-card-${rowKey}`}
          >
            <div className={modalStyles.mobileGrid}>
              <div className={modalStyles.mobileField}>
                <span className={modalStyles.mobileLabel}>Liberty Approval Needed?</span>
                <span className={modalStyles.mobileValue}>
                  <input
                    type="checkbox"
                    className={modalStyles.checkbox}
                    checked={checked}
                    disabled={sending || validating || !selectableRow}
                    onChange={() => onToggleOccupant(prospectKey)}
                    data-testid={`yardi-guest-invite-modal-select-${rowKey}`}
                    aria-label={`Liberty approval needed for ${name || prospectKey}`}
                  />
                </span>
              </div>
              <div className={modalStyles.mobileField}>
                <span className={modalStyles.mobileLabel}>Name</span>
                <span
                  className={modalStyles.mobileValue}
                  data-testid={`yardi-guest-invite-modal-cell-name-${rowKey}`}
                >
                  <ContactValue value={name} emptyLabel="No name" hasError={nameHasError} />
                </span>
              </div>
              <div className={modalStyles.mobileField}>
                <span className={modalStyles.mobileLabel}>Prospect ID</span>
                <span
                  className={modalStyles.mobileValue}
                  data-testid={`yardi-guest-invite-modal-cell-prospect-id-${rowKey}`}
                >
                  {String(row.prospectId ?? '').trim() || '—'}
                </span>
              </div>
              <div className={modalStyles.mobileField}>
                <span className={modalStyles.mobileLabel}>Tenant ID</span>
                <span
                  className={modalStyles.mobileValue}
                  data-testid={`yardi-guest-invite-modal-cell-tenant-id-${rowKey}`}
                >
                  {String(row.tenantId ?? '').trim() || '—'}
                </span>
              </div>
              <div className={modalStyles.mobileField}>
                <span className={modalStyles.mobileLabel}>Email</span>
                <span
                  className={modalStyles.mobileValue}
                  data-testid={`yardi-guest-invite-modal-cell-email-${rowKey}`}
                >
                  <ContactValue
                    value={email}
                    emptyLabel="No email"
                    hasError={emailHasError}
                    hasWarning={emailHasWarning}
                  />
                </span>
              </div>
              <div className={modalStyles.mobileField}>
                <span className={modalStyles.mobileLabel}>Phone</span>
                <span
                  className={modalStyles.mobileValue}
                  data-testid={`yardi-guest-invite-modal-cell-phone-${rowKey}`}
                >
                  <ContactValue
                    value={phone}
                    emptyLabel="No phone"
                    hasError={phoneHasError}
                    hasWarning={phoneHasWarning}
                  />
                </span>
              </div>
              <div className={modalStyles.mobileField}>
                <span className={modalStyles.mobileLabel}>Credit Check Status</span>
                <span
                  className={modalStyles.mobileValue}
                  data-testid={`yardi-guest-invite-modal-cell-credit-check-${rowKey}`}
                >
                  {displayCreditCheck(row.creditCheckStatus)}
                </span>
              </div>
              <div className={modalStyles.mobileField}>
                <span className={modalStyles.mobileLabel}>Relationship Type</span>
                <span
                  className={modalStyles.mobileValue}
                  data-testid={`yardi-guest-invite-modal-cell-relationship-${rowKey}`}
                >
                  <RelationshipBadge type={row.type} />
                </span>
              </div>
              {rowErrors.length > 0 ? (
                <div className={modalStyles.mobileField}>
                  <span className={modalStyles.mobileLabel}>{rowErrorColumnLabel}</span>
                  <span
                    className={modalStyles.mobileValue}
                    data-testid={`yardi-guest-invite-modal-cell-error-${rowKey}`}
                  >
                    <ValidationErrorCell
                      errors={rowErrors}
                      rowKey={rowKey}
                      level="applicant"
                      isLastRow={idx === occupants.length - 1}
                      tooltipSubtitle={
                        String(row.prospectId ?? '').trim()
                          ? `Prospect ID: ${String(row.prospectId).trim()}`
                          : undefined
                      }
                    />
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function YardiGuestInviteModal({
  isOpen,
  occupants,
  selectedProspectIds,
  sending,
  sendPhase = 'idle',
  validating = false,
  canSend = false,
  validationResult = null,
  onClose,
  onToggleOccupant,
  onSendInvite,
  onRevalidate,
}: YardiGuestInviteModalProps) {
  const isMobile = useIsMobile()

  const applicantErrorsByProspectId = new Map<string, YardiGuestInviteValidationError[]>()
  for (const applicant of validationResult?.applicants ?? []) {
    if (applicant.prospectId && applicant.errors.length > 0) {
      applicantErrorsByProspectId.set(applicant.prospectId.toUpperCase(), applicant.errors)
    }
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Invite Applicant"
      onClose={onClose}
      closeOnOverlayClick={false}
      hideClose={sending || validating}
      panelClassName={modalStyles.panel}
      headerClassName={modalStyles.header}
      headerVariant="iconTitle"
      titleIcon={<UserRound className="h-5 w-5" strokeWidth={2} />}
      bodyScrollOnly
      footerAlign="end"
      overlayDataTestId="yardi-guest-invite-modal-overlay"
      panelDataTestId="yardi-guest-invite-modal-panel"
      closeButtonDataTestId="yardi-guest-invite-modal-close"
      titleDataTestId="yardi-guest-invite-modal-title"
      subtitle={YARDI_GUEST_INVITE_CONTACT_INFO_MESSAGE}
      bodyClassName={modalStyles.body}
      footerClassName={modalStyles.footerChrome}
      footer={
        <ModalFooter
          sending={sending}
          sendPhase={sendPhase}
          validating={validating}
          canSend={canSend}
          selectedCount={selectedProspectIds.length}
          onClose={onClose}
          onRevalidate={onRevalidate}
          onSendInvite={onSendInvite}
        />
      }
    >
      <div className={modalStyles.bodyContent}>
        {validating ? (
          <div className={modalStyles.validatingOverlay} data-testid="yardi-guest-invite-modal-validating">
            <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" aria-label="Validating" />
            <span>Validating applicants…</span>
          </div>
        ) : null}

        <section
          className={`${modalStyles.section} ${modalStyles.applicationSection}`}
          data-testid="yardi-guest-invite-modal-application-section"
        >
          <h3 className={modalStyles.sectionTitle} data-testid="yardi-guest-invite-modal-application-section-title">
            Application
          </h3>
          <ApplicationLevelRow validationResult={validationResult} isMobile={isMobile} />
        </section>

        <section
          className={`${modalStyles.section} ${modalStyles.applicantSection}`}
          data-testid="yardi-guest-invite-modal-applicant-section"
        >
          <h3 className={modalStyles.sectionTitle} data-testid="yardi-guest-invite-modal-applicant-section-title">
            Applicant(s)
          </h3>
          {isMobile ? (
            <MobileOccupantCards
              occupants={occupants}
              selectedProspectIds={selectedProspectIds}
              sending={sending}
              validating={validating}
              applicantErrorsByProspectId={applicantErrorsByProspectId}
              onToggleOccupant={onToggleOccupant}
            />
          ) : (
            <DesktopOccupantTable
              occupants={occupants}
              selectedProspectIds={selectedProspectIds}
              sending={sending}
              validating={validating}
              applicantErrorsByProspectId={applicantErrorsByProspectId}
              onToggleOccupant={onToggleOccupant}
            />
          )}
        </section>
      </div>
    </OnScreenModal>
  )
}
